// `aggregate` pipeline verb — collapse rows into per-group aggregates.
//
// Surface:
//
//   (aggregate (out1 (sum revenue))
//              (out2 (count))
//              :by [region year])
//
// Each positional arg is a `(out-name (agg-call args))` pair. The
// agg-call dispatches through `aggMeta` (the agg-context registry —
// see `ops/transform/agg-funcs.ts`). `:by` is an optional vector of
// column-name symbols defining the grouping keys.
//
// Special-form because the args are AST pairs that the verb walks
// shape-first to dispatch through `aggMeta`. Eager evaluation of
// `(sum revenue)` would fail — `sum` is not in `opMeta`.
//
// Output schema: `[...by-cols, ...out-cols]`. By-col types preserve
// their input schema entries; out-col types default to `unknownCol`
// for now (AggOpMeta doesn't declare result types yet — refining
// later if a workflow needs precise inference; the runtime values
// flow correctly regardless).

import { z } from 'zod';

import { TypeMismatchError } from '../../../errors.ts';
import { astShape, opCallPos } from '../../../normalize.ts';
import { type ColumnRefInArg, pipelineVerbs, type SpecialVerbCtx } from '../../../op-meta.ts';
import { paramPath } from '../../../path.ts';
import { groupRowsBy, materializedToRows } from '../../../runtime/arquero.ts';
import { type ColumnType, type Schema, schemaFromEntries, unknownCol } from '../../../schema/types.ts';
import { type Cell, MaterializedDataset } from '../../../value.ts';
import { AggPairSchema, compileAggPair } from '../agg-funcs.ts';

/** Symbol shape — bare column symbol used in `:by`. */
// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const ColSymbol = z.object({ $sym: z.string().min(1) });

export const aggregate = z
  .object({
    name: z.literal('aggregate'),
    // `params` are out/agg pairs. Validate each is `(name agg-call)` shape.
    params: z.array(AggPairSchema).min(1),
    by: z.array(ColSymbol).optional(),
  })
  .describe(
    'Group rows and compute per-group aggregates. Positional args are `(out-name (agg-call …))` pairs; `:by` is a vector of column symbols (omit for global rollup).',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (aggregate (total (sum revenue)) (n (count)) :by [region]))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const byKeys = (data.by ?? []).map((s) => s.$sym);
      const pairs = (data.params as readonly z.infer<typeof AggPairSchema>[]).map((pair, i) =>
        compileAggPair(pair, paramPath(ctx.path, i)),
      );

      // Validate that every by-key column exists on the input schema.
      // Type-check pass should catch missing refs first, but the
      // dispatch path can still see mismatches if the schema diverges
      // from runtime.
      for (const key of byKeys) {
        if (!dataset.schema.has(key)) {
          throw new TypeMismatchError(
            `:by column \`${key}\` to exist on the threaded dataset`,
            'no such column',
            'aggregate',
            opCallPos(ctx.spans, ctx.path),
          );
        }
      }

      const rows = materializedToRows(dataset);
      const groups = groupRowsBy(rows, byKeys);
      const outRows = groups.map(({ representativeRow, rows: groupRows }) => {
        const out: Record<string, Cell> = { ...(representativeRow as Record<string, Cell>) };
        for (const { outName, reduce } of pairs) {
          out[outName] = reduce(groupRows) as Cell;
        }
        return out;
      });

      // Rows → columns. Schema-after dictates the column order.
      const cols: Record<string, Cell[]> = {};
      for (const k of byKeys) {
        cols[k] = outRows.map((r) => r[k] as Cell);
      }
      for (const { outName } of pairs) {
        cols[outName] = outRows.map((r) => r[outName] as Cell);
      }
      return new MaterializedDataset(cols, propagateAggregateSchema(data, dataset.schema, byKeys));
    },
    propagateSchema: (data, ctx): Schema => {
      const byKeys = ((data as z.infer<typeof aggregate>).by ?? []).map((s) => s.$sym);
      return propagateAggregateSchema(data as z.infer<typeof aggregate>, ctx.inputSchema, byKeys);
    },
    // `:by` columns are usages. The agg-call subtrees may reference
    // other columns (e.g. (sum revenue) → revenue); we report those
    // too so the typecheck pass can validate them AND the dispatch
    // wrapper's per-cell branch can detect a nested-dataset axis on
    // a TS-ref column.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const refs: ColumnRefInArg[] = [];
      const parsed = data as z.infer<typeof aggregate>;
      for (const sym of parsed.by ?? []) {
        refs.push({ name: sym.$sym, path: basePath });
      }
      parsed.params.forEach((pair, i) => {
        const aggCall = pair.params[0];
        const innerArgs = aggCall.params ?? [];
        innerArgs.forEach((arg, j) => {
          const shape = astShape(arg);
          if (shape.kind === 'symbolRef') {
            refs.push({ name: shape.sym, path: paramPath(paramPath(paramPath(basePath, i), 0), j) });
          }
        });
      });
      return refs;
    },
  });

/**
 * Build the output schema for an aggregate call. By-key columns
 * preserve their input types; agg outputs default to `unknownCol`
 * until AggOpMeta declares per-func result types (deferred — runtime
 * values flow correctly through `unknownCol`, the type-check pass
 * just can't refine downstream operations on the agg output).
 */
const propagateAggregateSchema = (
  data: z.infer<typeof aggregate>,
  inputSchema: Schema,
  byKeys: readonly string[],
): Schema => {
  const entries: [string, ColumnType][] = [];
  for (const k of byKeys) {
    const t = inputSchema.get(k);
    if (t !== undefined) {
      entries.push([k, t]);
    }
  }
  for (const pair of data.params) {
    entries.push([pair.name, unknownCol]);
  }
  return schemaFromEntries(entries);
};
